<template>
    <div class="support-discussion">
        <div v-if="!isDeviceLinked" class="support-discussion__container">
            <back-link class="back-link" to="/my/support">
                {{ $t('BACK_TO_SUPPORT_TICKETS') }}
            </back-link>
            <div class="support-discussion__requires-device">
                <p class="support-discussion__requires-device-text">{{ $t('SUPPORT_REQUIRES_LINKED_DEVICE') }}</p>
                <Button tag="router-link" to="/link" type="primary">{{ $t('LINK_YOUR_DEVICE') }}</Button>
            </div>
        </div>
        <div v-else class="support-discussion__container">
            <back-link class="back-link" to="/my/support">
                {{ $t('BACK_TO_SUPPORT_TICKETS') }}
            </back-link>

            <div v-if="ticketLoading" class="support-discussion__loading">
                <Loader />
            </div>
            <div v-else-if="ticket">
                <!-- Ticket Moderation Section -->
                <div v-if="ticket.is_moderator === 1" class="ticket-moderation">
                    <div class="ticket-moderation__header">
                        <h3 class="ticket-moderation__title">Ticket moderation</h3>
                        <button class="close-ticket-btn" @click="showCloseModal = true">Close ticket</button>
                    </div>
                    <div class="ticket-moderation__info">
                        <div class="ticket-moderation__email">Email: {{ ticket.email }}</div>
                    </div>
                    <div class="ticket-moderation__devices">
                        <table class="devices-table">
                            <tr v-for="device in ticket.devices" :key="device.provisioning_udid">
                                <td>
                                    <a :href="`https://appdb.to/?lt=${device.link_token}`" target="_blank">
                                        Device: {{ device.nice_idevice_model }} {{ device.os_version }}
                                    </a> <span class="provisioning-udid">{{
                                        device.provisioning_udid }}</span>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div class="support-discussion__header">
                    <h1 class="support-discussion__title">{{ ticket.topic }}</h1>
                    <div class="support-discussion__meta">
                        <div class="support-discussion__date">
                            {{ $t('CREATED') }}: {{ formatDate(ticket.added_at) }}
                        </div>
                        <div class="support-discussion__status"
                            :class="{ 'support-discussion__status--closed': ticket.status === 'closed' }">
                            {{ ticket.status_translated }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="support-discussion__content">
                <Discussion v-if="discussionTopic" :topic="discussionTopic" :isPrivate="true" :forceType="'private'"
                    :canCommentAsUser="!!$store.state.customerProfile?.email"
                    :canCommentAsStaff="!!$store.state.publisherProfile?.email" :disallowNestedComments="true" />
                <div v-else class="support-discussion__loading">
                    <Loader />
                </div>
            </div>
        </div>

        <!-- Close Ticket Modal -->
        <div v-if="showCloseModal" class="modal-backdrop" @click.self="showCloseModal = false">
            <div class="modal-content">
                <h3 class="modal-title">Close Ticket</h3>
                <p class="modal-message">Are you really want to close this ticket?</p>

                <div class="modal-checkbox">
                    <input type="checkbox" id="deleteTicket" v-model="deleteTicket">
                    <label for="deleteTicket">Delete it as nonsense</label>
                </div>

                <div class="modal-actions">
                    <button class="modal-cancel" @click="showCloseModal = false">Cancel</button>
                    <button class="modal-confirm" @click="closeTicket">Confirm</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Discussion from '@/components/Discussion.vue';
import BackLink from '@/components/BackLink.vue';
import Loader from '@/components/Loader.vue';
import Button from '@/components/Button.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'SupportDiscussion',
  components: {
    Discussion,
    BackLink,
    Loader,
    Button,
  },
  metaInfo() {
    const ticketText = this.ticket ? this.$t('SUPPORT_TICKET_WITH_ID', { id: this.ticketId, topic: this.ticket.topic }) : this.$t('SUPPORT_TICKET');
    return {
      title: ticketText,
      meta: [
        {
          name: 'description',
          content: this.$t('SUPPORT_TICKET_META_DESCRIPTION'),
        },
      ],
    };
  },
  data() {
    return {
      discussionTopic: null,
      ticket: null,
      ticketLoading: true,
      pollTimeout: null,
      isPolling: false,
      showCloseModal: false,
      deleteTicket: false,
      closingTicket: false,
    };
  },
  computed: {
    ...mapGetters(['isDeviceLinked']),
    ticketId() {
      const match = this.$route.path.match(/\/my\/support\/discussion=(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    },
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      return date.toLocaleString();
    },

    async fetchTicketDetails() {
      if (!this.ticketId) {
        this.$router.push('/my/support');
        return;
      }

      this.ticketLoading = true;

      try {
        const response = await this.$api.getSupportTickets(this.ticketId);

        if (response.success && response.data && response.data.length > 0) {
          this.ticket = response.data[0];
          this.discussionTopic = `support_${this.ticketId}`;
        } else {
          this.$notify({
            type: 'error',
            title: this.$t('ERROR'),
            text: this.$t('TICKET_NOT_FOUND'),
          });
          this.$router.push('/my/support');
        }
      } catch (error) {
        console.error('Error fetching ticket details:', error);
        this.$notify({
          type: 'error',
          title: this.$t('ERROR'),
          text: this.$t('FAILED_TO_LOAD_TICKET_DETAILS'),
        });
        this.$router.push('/my/support');
      } finally {
        this.ticketLoading = false;
      }
    },

    closeTicket() {
      if (this.closingTicket) return;
      this.closingTicket = true;

      this.$api.post('close_support_ticket', {
        id: this.ticketId,
        st: this.$store.state.st,
        with_deletion: this.deleteTicket ? 1 : 0,
      })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: 'Ticket has been closed successfully',
          });

          // Update the local ticket status
          this.ticket.status = 'closed';
          this.ticket.status_translated = 'Closed';

          // Close the modal
          this.showCloseModal = false;
          this.deleteTicket = false;

          // If ticket was deleted, go back to support page
          if (this.deleteTicket) {
            this.$router.push('/my/support');
          }
        })
        .catch((error) => {
          console.error('Error closing ticket:', error);
          this.$notify({
            type: 'error',
            title: this.$t('ERROR'),
            text: 'Failed to close the ticket. Please try again.',
          });
        })
        .finally(() => {
          this.closingTicket = false;
        });
    },

    async fetchMessages() {
      if (!this.discussionTopic) return;

      try {
        const response = await this.$api.getMessages({
          topic: this.discussionTopic,
          lt: this.$store.state.lt,
        });

        // Find the Discussion component and update its messages directly
        const discussionComponent = this.$children.find(child => child.$options.name === 'Discussion');
        if (discussionComponent) {
          discussionComponent.messages = response.data || [];
          discussionComponent.messagesLoaded = true;
          discussionComponent.total = response.total || 0;
        }

        // Refresh device info
        await this.$store.dispatch('getDevices');

        // Schedule next poll if we're still polling
        if (this.isPolling) {
          this.pollTimeout = setTimeout(() => {
            this.fetchMessages();
          }, 15000);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        // Stop polling on error
        this.isPolling = false;
        clearTimeout(this.pollTimeout);
      }
    },

    startPolling() {
      this.isPolling = true;
      this.fetchMessages();
    },

    stopPolling() {
      this.isPolling = false;
      clearTimeout(this.pollTimeout);
    },
  },
  mounted() {
    if (this.isDeviceLinked) {
      this.fetchTicketDetails();
      this.startPolling();
    } else {
      this.ticketLoading = false;
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style lang="scss" scoped>
.support-discussion {
    padding: 10px;
    width: 100%;

    @media (min-width: 768px) {
        padding: 20px;
    }

    &__container {
        position: relative;
        width: 100%;
        max-width: 1400px;
        margin: 0 auto;
        background-color: var(--bg-primary);
        border-radius: 8px;
        padding: 15px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

        @media (min-width: 768px) {
            padding: 25px;
            width: 90%;
        }
    }

    &__requires-device {
        text-align: center;
        padding: 24px 8px 8px;
    }

    &__requires-device-text {
        color: var(--text-secondary);
        font-size: 16px;
        line-height: 1.5;
        margin-bottom: 20px;
    }

    &__header {
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--border-color);

        @media (min-width: 768px) {
            margin-bottom: 30px;
        }
    }

    &__title {
        margin-bottom: 10px;
        font-size: 20px;
        font-weight: 600;
        word-break: break-word;

        @media (min-width: 768px) {
            font-size: 24px;
        }
    }

    &__meta {
        display: flex;
        flex-direction: column;
        gap: 8px;
        color: var(--text-secondary);
        font-size: 14px;

        @media (min-width: 768px) {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
        }
    }

    &__content {
        min-height: 300px;

        @media (min-width: 768px) {
            min-height: 500px;
        }
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 200px;
        color: var(--text-secondary);
    }

    &__status {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        min-width: 70px;
        text-align: center;

        /* Status styling - using the same style as in support table */
        background-color: var(--lighter-primary);
        color: var(--primary);
        border: 1px solid rgba(0, 0, 0, 0.05);

        &--closed {
            background-color: var(--bg-success);
            color: black;
        }
    }
}

.back-link {
    display: block;
    margin-bottom: 15px;
    color: var(--text-primary);

    &:hover {
        color: var(--primary);
    }
}

/* Ticket Moderation Styles */
.ticket-moderation {
    margin-bottom: 20px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 2px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
    }

    &__title {
        font-size: 14px;
        font-weight: 600;
        color: #495057;
        letter-spacing: 0.5px;
        margin: 0;
    }

    &__info {
        margin-bottom: 10px;
    }

    &__email {
        font-size: 13px;
        color: #495057;
        margin-bottom: 8px;
    }

    &__devices {
        overflow-x: auto;
    }
}

.close-ticket-btn {
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 12px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;

    &:hover {
        background-color: #c82333;
    }
}

.devices-table {
    width: 100%;
    border-collapse: collapse;

    td {
        padding: 6px 8px;
        font-size: 13px;
        border-bottom: 1px solid #dee2e6;
    }

    a {
        color: var(--primary);
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
}

.provisioning-udid {
    color: #6c757d;
    background-color: rgba(255, 255, 255, 0.7);
    padding: 1px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 12px;
}

/* Modal Styles */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    width: 90%;
    max-width: 400px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    color: #212529;
}

.modal-title {
    font-size: 18px;
    margin-top: 0;
    margin-bottom: 15px;
    color: #212529;
}

.modal-message {
    margin-bottom: 20px;
    color: #495057;
}

.modal-checkbox {
    margin-bottom: 20px;
    display: flex;
    align-items: center;

    input[type="checkbox"] {
        margin-right: 8px;
        width: 18px;
        height: 18px;
        accent-color: #dc3545;
        border: 2px solid #6c757d;
        cursor: pointer;
        position: relative;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-color: #fff;
        border-radius: 3px;
        outline: none;

        &:checked {
            background-color: #dc3545;
            border-color: #dc3545;
        }

        &:checked::after {
            content: '✓';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 14px;
            color: white;
            font-weight: bold;
        }

        &:hover {
            border-color: #dc3545;
        }
    }

    label {
        font-size: 14px;
        color: #212529;
        cursor: pointer;
    }
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.modal-cancel,
.modal-confirm {
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    border: none;
}

.modal-cancel {
    background-color: #f8f9fa;
    color: #495057;
    border: 1px solid #ced4da;

    &:hover {
        background-color: #e9ecef;
    }
}

.modal-confirm {
    background-color: #dc3545;
    color: white;

    &:hover {
        background-color: #c82333;
    }
}
</style>
